import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useCallback, useEffect, useState } from "react";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Button } from "@progress/kendo-react-buttons";
import { closeDialog } from "../../components/dialog/dialogSlice";
import AppDialog from "../../components/dialog/Dialog";
import FormIncrementalSearch from "../../components/formFields/FormIncrementalSearch";
import { getAllItemIncremental } from "../Item/services/item.services";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import { IItem } from "../Item/itemModel";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import {
  clearExportPackingItemDetail,
  clearExportPackingItemID,
} from "./exportPackingSlice";
import FormTextField from "../../components/formFields/FormTextField";
import {
  getExportPackingItemByID,
  updateExportPackingItem,
} from "./services/exportPacking.services";
import { getExportSalesOrderByPONO } from "../exportsalesorder/services/exporSalesOrder.services";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IUnit } from "../unit/unitModel";
import { findAllActiveUnit } from "../unit/services/unit.services";

interface FormChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const ExportPackingItemChangeWatcher: React.FC<FormChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const item_options = formRenderProps.valueGetter("item_options");
  const conversionUnitWeight = formRenderProps.valueGetter(
    "conversion_unit_for_weight"
  );
  const item_id = formRenderProps.valueGetter("item_id");
  const quantity_ctn = formRenderProps.valueGetter("quantity_ctn") || 0;
  const packing_weight = formRenderProps.valueGetter("packing_weight") || 0;
  const gross_kg = formRenderProps.valueGetter("gross_kg") || 0;
  const net_kg = formRenderProps.valueGetter("net_kg") || 0;
  const size_c = formRenderProps.valueGetter("size_c") || 0;
  const size_b = formRenderProps.valueGetter("size_b") || 0;
  const size_m = formRenderProps.valueGetter("size_m") || 0;
  const total = formRenderProps.valueGetter("total") || 0;

  const conversion_unit_for_weight = item_options?.find(
    (item: any) => item.id === item_id
  )?.conversion_unit_for_weight;
  useEffect(() => {
    if (item_id) {
      formRenderProps.onChange("total_weight", {
        value:
          (conversion_unit_for_weight || conversionUnitWeight) *
            packing_weight *
            quantity_ctn || null,
      });
    }
  }, [item_id, packing_weight, quantity_ctn, conversion_unit_for_weight]);

  useEffect(() => {
    formRenderProps.onChange("gross_weight", {
      value: gross_kg * quantity_ctn || null,
    });
  }, [gross_kg, quantity_ctn]);

  useEffect(() => {
    formRenderProps.onChange("net_weight", {
      value: net_kg * quantity_ctn || null,
    });
  }, [net_kg, quantity_ctn]);

  useEffect(() => {
    formRenderProps.onChange("net_kg", {
      value:
        +(conversion_unit_for_weight || conversionUnitWeight) *
          +packing_weight || 0,
    });
  }, [packing_weight, conversion_unit_for_weight, conversionUnitWeight]);

  useEffect(() => {
    formRenderProps.onChange("total", {
      value: (size_c * size_b * size_m) / 1000000,
    });
  }, [size_c, size_b, size_m]);

  useEffect(() => {
    formRenderProps.onChange("sub_total", {
      value: total * quantity_ctn,
    });
  }, [total, quantity_ctn]);

  return null;
};

const UpdateExportPackingItem: React.FC = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.exportpacking.loading);
  const ExportPackingItemID = useAppSelector(
    (state) => state.exportpacking.ExportPackingItemID
  );
  const PoNo = useAppSelector((state) => state.exportpacking.PoNo);
  const ExportPackingItemDetail = useAppSelector(
    (state) => state.exportpacking.ExportPackingItemDetail
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const UnitList = useAppSelector((state) => state.unit.UnitList);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ExportPackingItemDetail]);

  useEffect(() => {
    if (ExportPackingItemID) {
      dispatch(getExportPackingItemByID(ExportPackingItemID));
    }
  }, [ExportPackingItemID]);

  useEffect(() => {
    dispatch(findAllActiveUnit());
    return () => {
      dispatch(clearExportPackingItemDetail());
      dispatch(clearExportPackingItemID());
    };
  }, []);

  const handleItemSearchChange = useCallback(
    async (search: string, field: string, formRenderProps: FormRenderProps) => {
      const result = await dispatch(getAllItemIncremental({ search }));
      formRenderProps.onChange(field, {
        value: result.payload,
      });
    },
    [dispatch]
  );

  const handleUnitChange = (e: any, formRenderProps: FormRenderProps) => {
    const item_options = formRenderProps.valueGetter("item_options");

    if (e?.value && item_options && item_options?.length > 0) {
      formRenderProps.onChange("unit_id", {
        value: item_options?.find((item: any) => item.id === e.value)?.unit_id,
      });
    }
  };

  const handleSubmit = async (values: any) => {
    const payload = {
      export_packing_guid: ExportPackingItemID ? ExportPackingItemID : "",
      export_sales_order_item_id: values?.export_sales_order_item_id
        ? +values?.export_sales_order_item_id
        : null,
      item_id: values?.item_id ? +values?.item_id : null,
      packing_size: values?.packing_size ? +values?.packing_size : null,
      unit_id: values?.unit_id ? +values?.unit_id : null,
      packing_unit_id: values?.packing_unit_id
        ? +values?.packing_unit_id
        : null,
      packing_weight: values?.packing_weight ? +values?.packing_weight : null,
      total_weight: values?.total_weight ? +values?.total_weight : null,
      sr_no: values?.sr_no ? values?.sr_no : "",
      size_c: values?.size_c ? +values?.size_c : null,
      size_b: values?.size_b ? +values?.size_b : null,
      size_m: values?.size_m ? +values?.size_m : null,
      total: values?.total ? +values?.total : null,
      sub_total: values?.sub_total ? +values?.sub_total?.toFixed(3) : null,
      gross_kg: values?.gross_kg ? +values?.gross_kg : null,
      net_kg: values?.net_kg ? +values?.net_kg : null,
      gross_weight: values?.gross_weight ? +values?.gross_weight : null,
      net_weight: values?.net_weight ? +values?.net_weight : null,
      quantity_ctn: values?.quantity_ctn ? +values?.quantity_ctn : null,
    };

    try {
      const res = await dispatch(updateExportPackingItem(payload));
      if (res.meta?.requestStatus === "fulfilled") {
        dispatch(closeDialog());
        dispatch(getExportSalesOrderByPONO(`${PoNo}`));
        dispatch(clearExportPackingItemDetail());
        dispatch(clearExportPackingItemID());
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const formCancel = () => {
    dispatch(closeDialog());
    dispatch(clearExportPackingItemDetail());
    dispatch(clearExportPackingItemID());
  };

  return (
    <>
      {dialogName === "updateExportPackingItem" && (
        <AppDialog>
          <Form
            key={formKey}
            onSubmit={handleSubmit}
            initialValues={ExportPackingItemDetail}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement style={{ minWidth: 600, maxWidth: 900 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 10, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <p
                      className="fs-4"
                      style={{ margin: 0, fontWeight: "bold" }}
                    >
                      Update Export Packing Item
                    </p>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <hr style={{ margin: "5px 0 0" }} />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="item_id"
                      label="Item Name"
                      placeholder="Search Item"
                      component={FormIncrementalSearch}
                      fetchIncrementalData={(search: any) =>
                        handleItemSearchChange(
                          search,
                          `item_options`,
                          formRenderProps
                        )
                      }
                      disabled={true}
                      validator={requiredValidator}
                      astrike={true}
                      onChange={(e: any) =>
                        handleUnitChange(e, formRenderProps)
                      }
                      options={
                        formRenderProps
                          .valueGetter("item_options")
                          ?.map((item: IItem) => {
                            return {
                              value: item.id,
                              label: item.product_name,
                            };
                          }) || []
                      }
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{ display: "flex", gap: 10 }}
                  >
                    <div className="k-form-field w-100">
                      <label className="k-label">Packing Size</label>
                      <span
                        className="k-input k-numerictextbox k-input-lg  k-rounded-lg ps-2 pe-2 d-flex align-items-center"
                        style={{ border: "1px solid #00000014" }}
                      >
                        {ExportPackingItemDetail?.packing_size}{" "}
                        {ExportPackingItemDetail?.unit_name}
                      </span>
                    </div>
                    <Field
                      wrapperClassName="w-100"
                      name="packing_weight"
                      label="Weight"
                      placeholder="0"
                      component={FormNumericTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                    <Field
                      wrapperClassName="w-100"
                      name="packing_unit_id"
                      label="Packing Unit"
                      placeholder="Select Packing Unit"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={UnitList?.map((unit: IUnit) => {
                        return {
                          value: unit?.id,
                          label: unit?.unit_code,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperClassName="w-100"
                      name="quantity_ctn"
                      label="Quantity (CTNS)"
                      placeholder="0"
                      component={FormNumericTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="total_weight"
                      label="Total (KG)"
                      placeholder="0"
                      component={FormNumericTextField}
                      disabled={true}
                      format="n3"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="sr_no"
                      label="Sr. No."
                      placeholder="Sr. No."
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{ display: "flex", gap: 10 }}
                  >
                    <Field
                      name="size_c"
                      label="C"
                      placeholder="0"
                      type="number"
                      component={FormTextField}
                    />
                    <Field
                      name="size_b"
                      label="B"
                      placeholder="0"
                      type="number"
                      component={FormTextField}
                    />
                    <Field
                      name="size_m"
                      label="M"
                      placeholder="0"
                      type="number"
                      component={FormTextField}
                    />
                    <Field
                      name="total"
                      placeholder="0"
                      label="Total"
                      component={FormTextField}
                      disabled={true}
                    />
                    <Field
                      name="sub_total"
                      placeholder="0"
                      label="Sub Total"
                      format="n3"
                      component={FormNumericTextField}
                      disabled={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{ display: "flex", gap: 10 }}
                  >
                    <Field
                      name="gross_kg"
                      label="Gross KG"
                      placeholder="0"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      format="n3"
                    />
                    <Field
                      name="net_kg"
                      label="Net KG"
                      placeholder="0"
                      disabled={true}
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      format="n3"
                    />
                    <Field
                      name="gross_weight"
                      label="Gross Weight"
                      placeholder="0"
                      disabled={true}
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      format="n3"
                    />
                    <Field
                      name="net_weight"
                      label="Net Weight"
                      placeholder="0"
                      disabled={true}
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      format="n3"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <div
                      className="col-12"
                      style={{
                        textAlign: "end",
                        marginTop: "20px",
                        width: "100%",
                      }}
                    >
                      <ButtonWithLoading
                        label={"Update"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 4 }}
                        onClick={formCancel}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
                <ExportPackingItemChangeWatcher
                  formRenderProps={formRenderProps}
                />
              </FormElement>
            )}
          />
        </AppDialog>
      )}
    </>
  );
};

export default UpdateExportPackingItem;
